import React, { createContext, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { removeMaterial, uploadMaterials } from "store/features/materialSpecs/specs";
import { MaterialSpecificationViewModel } from "api/factories/creatives.factory";
import { getTokensMatSpecId } from "store/features/authorization";
import { getLocalStorageIsDoNotShowWelcomeTab, getMaterialType } from "utils";
import { useTranslation } from "react-i18next";

export type TabTypes =
    | "WELCOME_TAB"
    | "DETAILS"
    | "VALIDATION"
    | "ANNOTATIONS"
    | "THIRD_PARTY_TAG"
    | "CUSTOMER_SUMMARY"
    | "HISTORY"
    | "ASSETS"
    | "VERSIONS";

interface MaterialSpecsContextProviderProps extends MaterialSpecsProviderState {
    remove(materialSpecId: number, materialId: number): void;
    setTab(tabId: TabTypes): void;
    goBack(): void;
    setPreview(preview: string, format: string, materialSpecId?: number, materialId?: number, assetId?: number): void;
    switchVisual(isVisual: boolean): void;
    select(isPrinted: boolean, materialSpecId: number, materialId: number, tabId?: TabTypes): void;
    dropOptionalMaterials(materialSpecId: number): (acceptedFiles: File[]) => void;
    dropExistingMaterials(materialSpecId: number, materialId: number): (acceptedFiles: File[]) => void;
    selectLast(materialSpecId: number): void;
    selectOptional(isPrinted: boolean, materialSpecId: number): void;
    setAsset(assetId: number): void;
    setPreviewVisibility(isPreviewVisible: boolean, materialSpecId?: number, materialId?: number): void;
    updateMaterialIds(materialSpecId: number, materialId: number): void;
    setDefaultFilter(name: string, value: string): void;
}

interface MaterialSpecsProviderState {
    isPreviewVisible: boolean;
    isPrinted: boolean;
    isOptionalSelected: boolean;
    selectedMaterialSpecId?: number;
    selectedMaterialId?: number;
    assetId?: number;
    preview: string;
    format: string;
    type: MaterialType;
    tab: TabTypes;
    isVisualVisible: boolean;
    customerNote?: string;
    defaultFilter: {
        name: string;
        value: string;
    };
}

export type MaterialType = "IMAGE" | "VIDEO" | "AUDIO" | "EPS" | "PDF" | "HTML" | "UNKNOWN";

export const MaterialSpecsContext = createContext({} as MaterialSpecsContextProviderProps);

export const MaterialSpecsProvider: React.FC<{ materialSpecifications: MaterialSpecificationViewModel[] }> = ({
    children,
    materialSpecifications,
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const materialSpecId = useSelector(getTokensMatSpecId);
    const [state, setState] = useState<MaterialSpecsProviderState>({
        isPreviewVisible: false,
        isPrinted: false,
        isOptionalSelected: false,
        preview: "",
        format: "",
        type: "UNKNOWN",
        tab: "WELCOME_TAB",
        isVisualVisible: false,
        defaultFilter: {
            name: t("buttons.label.all"),
            value: "",
        },
    });

    useEffect(() => {
        if (materialSpecifications.length > 0) {
            const materialSpec = materialSpecifications.find(({ id }) => id === materialSpecId);

            // if material has been found
            if (materialSpec) {
                const [firstSpecMaterial] = materialSpec.materials;
                // don't open summary since it's disabled for empty material
                const isMaterialEmpty = firstSpecMaterial.inputFileName === "";
                const defaultTab = isMaterialEmpty ? "THIRD_PARTY_TAG" : "CUSTOMER_SUMMARY";

                return setState((state) => ({
                    ...state,
                    isPrinted: materialSpec.isPrinted,
                    selectedMaterialSpecId: materialSpec.id,
                    selectedMaterialId: firstSpecMaterial.id,
                    customerNote: materialSpec.customerNote,
                    preview: "",
                    format: "",
                    type: "UNKNOWN",
                    isOptionalSelected: false,
                    tab: getLocalStorageIsDoNotShowWelcomeTab() ? defaultTab : "WELCOME_TAB",
                }));
            }

            // in other case take first element
            const [defaultMaterialSpec] = materialSpecifications;

            setState((state) => ({
                ...state,
                isPrinted: defaultMaterialSpec.isPrinted,
                selectedMaterialSpecId: defaultMaterialSpec.id,
                selectedMaterialId: defaultMaterialSpec.materials[0].id,
                customerNote: defaultMaterialSpec.customerNote,
                preview: "",
                format: "",
                type: "UNKNOWN",
                isOptionalSelected: false,
                tab: "CUSTOMER_SUMMARY",
            }));
        }
        // eslint-disable-next-line
    }, [materialSpecifications.length, materialSpecId]);

    const updateMaterialIds = useCallback((selectedMaterialSpecId: number, selectedMaterialId: number) => {
        setState((state) => ({ ...state, selectedMaterialSpecId, selectedMaterialId }));
    }, []);

    const handleTabChange = useCallback((tabId: TabTypes) => setState((state) => ({ ...state, tab: tabId })), []);

    const handleSelect = useCallback(
        (isPrinted: boolean, selectedMaterialSpecId: number, selectedMaterialId: number, tab?: TabTypes) => {
            const isAlreadySelected = selectedMaterialId === state.selectedMaterialId;

            if (isAlreadySelected) {
                return;
            }

            let isSelectedMaterialThirdParty = false;
            let isMaterialEmpty = false;
            const materialSpec = materialSpecifications.find((spec) => spec.id === selectedMaterialSpecId);
            if (materialSpec) {
                const selectedMaterial = materialSpec.materials.find((material) => material.id === selectedMaterialId);
                isSelectedMaterialThirdParty = !!selectedMaterial && selectedMaterial.isThirdPartyTagMaterial;
                isMaterialEmpty = selectedMaterial?.inputFileName === "";
            }

            setState((state) => {
                let newTab = tab;

                // so.. if specific tab is not specified:
                // show welcome tab as default only on load, so change to the default one
                // if selected tab is third party and another one is third party than leave as it is - it's fine
                // if selected tab is third party and newly selected material is NOT third party, than change to customer summary tab
                // otherwise it will open by default the first tab which is welcome tab, and we want to avoid that
                // in any other case, just leave the tab that was open
                if (!newTab) {
                    if (state.tab === "WELCOME_TAB") {
                        newTab = "CUSTOMER_SUMMARY";
                    } else if (state.tab === "THIRD_PARTY_TAG") {
                        if (isSelectedMaterialThirdParty || isMaterialEmpty) {
                            newTab = "THIRD_PARTY_TAG";
                        } else {
                            newTab = "CUSTOMER_SUMMARY";
                        }
                    } else {
                        newTab = state.tab;
                    }
                }

                return {
                    ...state,
                    // TODO: for now always change to customer summary or third party
                    isOptionalSelected: false,
                    isPrinted,
                    selectedMaterialSpecId,
                    selectedMaterialId,
                    isVisualVisible: false,
                    assetId: undefined,
                    customerNote:
                        selectedMaterialSpecId === state.selectedMaterialSpecId
                            ? state.customerNote
                            : materialSpecifications.find((matSpec) => matSpec.id === selectedMaterialSpecId)
                                  ?.customerNote,
                    tab: newTab,
                };
            });
        },
        [state.selectedMaterialId, materialSpecifications]
    );

    const handleGoBack = useCallback(
        () =>
            setState((state) => ({
                ...state,
                isPreviewVisible: false,
                preview: "",
                assetId: undefined,
                isVisualVisible: false,
            })),
        []
    );

    const handlePreview = useCallback(
        (
            preview: string,
            format: string,
            selectedMaterialSpecId?: number,
            selectedMaterialId?: number,
            assetId?: number
        ) =>
            setState((state) => ({
                ...state,
                isPreviewVisible: true,
                selectedMaterialId: selectedMaterialId || state.selectedMaterialId, // change material spec id on preview
                selectedMaterialSpecId: selectedMaterialSpecId || state.selectedMaterialSpecId, // change material id on preview
                preview,
                format,
                type: getMaterialType(format),
                tab:
                    selectedMaterialId !== state.selectedMaterialId
                        ? "CUSTOMER_SUMMARY"
                        : state.tab === "WELCOME_TAB"
                        ? "CUSTOMER_SUMMARY"
                        : state.tab,
                assetId,
                isVisualVisible: !!assetId,
            })),
        []
    );

    const handleRemove = useCallback(
        (materialSpecId: number, materialId: number) => {
            dispatch(
                removeMaterial.request({
                    materialSpecId,
                    materialId,
                    updateMaterialIds,
                })
            );

            setState((state) => ({ ...state, tab: "CUSTOMER_SUMMARY" }));
        },
        [dispatch, updateMaterialIds]
    );

    const handleDropOptionalMaterials = useCallback(
        (materialSpecId: number) => (acceptedFiles: File[]) => {
            dispatch(
                uploadMaterials.request({
                    files: acceptedFiles,
                    materialSpecId,
                    updateMaterialIds,
                    selectedTab: state.tab,
                })
            );
            handleTabChange("CUSTOMER_SUMMARY");
        },
        [dispatch, handleTabChange, updateMaterialIds, state.tab]
    );

    const handleDropExistingMaterials = useCallback(
        (materialSpecId: number, materialId: number) => (acceptedFiles: File[]) => {
            dispatch(
                uploadMaterials.request({
                    files: acceptedFiles,
                    materialSpecId,
                    materialId,
                    updateMaterialIds,
                    selectedTab: state.tab,
                })
            );
            handleTabChange("CUSTOMER_SUMMARY");
        },
        [dispatch, handleTabChange, updateMaterialIds, state.tab]
    );

    const handleSelectLast = useCallback(
        (materialSpecId: number) => {
            const materialSpec = materialSpecifications.find(({ id }) => id === materialSpecId);

            // this must be always defined
            if (materialSpec) {
                // get last material (it's always optional)
                const lastMaterial = materialSpec.materials[materialSpec.materials.length - 1];
                // print material don't have optional upload
                handleSelect(false, materialSpecId, lastMaterial.id);
            }
        },
        [handleSelect, materialSpecifications]
    );

    const handleOptionalSelected = useCallback((isPrinted: boolean, materialSpecId: number) => {
        setState((state) => ({
            ...state,
            isOptionalSelected: true,
            tab: "THIRD_PARTY_TAG",
            isPrinted,
            selectedMaterialSpecId: materialSpecId,
            selectedMaterialId: undefined,
        }));
    }, []);

    const handleSwitchVisual = useCallback(
        (isVisualVisible: boolean) =>
            setState((state) => ({ ...state, isVisualVisible, assetId: isVisualVisible ? state.assetId : undefined })),
        []
    );

    const handleAsset = useCallback((assetId: number) => setState((state) => ({ ...state, assetId })), []);

    const handlePreviewVisibility = useCallback(
        (
            isPreviewVisible: boolean,
            selectedMaterialSpecId = state.selectedMaterialSpecId,
            selectedMaterialId = state.selectedMaterialId
        ) => setState((state) => ({ ...state, isPreviewVisible, selectedMaterialSpecId, selectedMaterialId })),
        [state.selectedMaterialSpecId, state.selectedMaterialId]
    );

    const handleDefaultFilter = useCallback(
        (name: string, value) => setState((state) => ({ ...state, defaultFilter: { name, value } })),
        []
    );

    return (
        <MaterialSpecsContext.Provider
            value={{
                ...state,
                updateMaterialIds,
                setTab: handleTabChange,
                select: handleSelect,
                setPreview: handlePreview,
                setPreviewVisibility: handlePreviewVisibility,
                switchVisual: handleSwitchVisual,
                setAsset: handleAsset,
                goBack: handleGoBack,
                remove: handleRemove,
                dropOptionalMaterials: handleDropOptionalMaterials,
                dropExistingMaterials: handleDropExistingMaterials,
                selectLast: handleSelectLast,
                selectOptional: handleOptionalSelected,
                setDefaultFilter: handleDefaultFilter,
            }}>
            {children}
        </MaterialSpecsContext.Provider>
    );
};
